// redux/reducers/quotationsReducer.js
const initialState = {
    quotations: [],
    currentQuotation: null,
    loading: false
};

// Load from localStorage on init
const loadQuotationsFromStorage = () => {
    const stored = localStorage.getItem('hpos_quotations');
    if (stored) {
        try {
            return JSON.parse(stored);
        } catch (e) {
            return [];
        }
    }
    return [];
};

// Save to localStorage helper
const saveQuotationsToStorage = (quotations) => {
    localStorage.setItem('hpos_quotations', JSON.stringify(quotations));
};

// Generate unique ID
const generateId = () => {
    return `QT_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Generate quotation number
const generateQuotationNumber = () => {
    const year = new Date().getFullYear();
    const month = String(new Date().getMonth() + 1).padStart(2, '0');
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `QT-${year}${month}-${random}`;
};

export const QUOTATION_STATUS = {
    DRAFT: "DRAFT",
    SENT: "SENT",
    PENDING: "PENDING",
    APPROVED: "APPROVED",
    CONVERTED: "CONVERTED",
    REJECTED: "REJECTED",
    REVISION_REQUESTED: "REVISION_REQUESTED"
};

const hasQuotationLines = (q) =>
    Array.isArray(q?.lineItems) && q.lineItems.length > 0;

/** Prefer richer lineItems / detail-fetched stamp so Sync/list replace don't wipe them.
 * Server dates win when present; keep existing dates when Sync/list row is thin. */
const mergeQuotationPreserveLines = (existing, incoming) => {
    if (!incoming) return existing || null;
    if (!existing) return incoming;
    const keepLines =
        !hasQuotationLines(incoming) && hasQuotationLines(existing)
            ? existing.lineItems
            : incoming.lineItems;
    const keepFetched = Boolean(existing.__detailFetched || incoming.__detailFetched);
    const createdAt = incoming.createdAt || existing.createdAt || null;
    const updatedAt = incoming.updatedAt || existing.updatedAt || createdAt;
    return {
        ...existing,
        ...incoming,
        lineItems: keepLines ?? existing.lineItems ?? incoming.lineItems ?? [],
        ...(createdAt ? { createdAt } : {}),
        ...(updatedAt ? { updatedAt } : {}),
        ...(keepFetched ? { __detailFetched: true } : {}),
    };
};

export const quotationsReducer = (state = { ...initialState, quotations: loadQuotationsFromStorage() }, action) => {
    let newQuotations;

    switch (action.type) {
        case "QUOTATIONS_LOADING":
            return { ...state, loading: action.payload };

        case "GET_QUOTATIONS":
            return { ...state, quotations: loadQuotationsFromStorage(), loading: false };

        case "SET_QUOTATIONS": {
            // Replace current page in memory, but keep previously hole-filled lineItems.
            const incoming = Array.isArray(action.payload) ? action.payload : [];
            const prevById = new Map(
                (state.quotations || []).map((q) => [String(q.POID || q.id || ""), q])
            );
            const quotations = incoming.map((q) => {
                const id = String(q?.POID || q?.id || "");
                if (!id) return q;
                return mergeQuotationPreserveLines(prevById.get(id), q);
            });
            return { ...state, quotations, loading: false };
        }

        case "UPSERT_QUOTATION": {
            const incoming = action.payload;
            if (!incoming) return state;
            const incomingId = String(incoming.POID || incoming.id || "");
            if (!incomingId || incomingId === "null") return state;
            const existing = (state.quotations || []).find(
                (q) => String(q.POID || q.id) === incomingId
            );
            const merged = mergeQuotationPreserveLines(existing, incoming);
            const quotations = existing
                ? (state.quotations || []).map((q) =>
                    String(q.POID || q.id) === incomingId ? merged : q
                )
                : [merged, ...(state.quotations || [])];
            return { ...state, quotations, loading: false };
        }

        case "GET_QUOTATION":
            const quotation = loadQuotationsFromStorage().find(q => (q.POID || q.id) === action.payload);
            return { ...state, currentQuotation: quotation, loading: false };

        case "CREATE_QUOTATION": {
            const newQuotation = action.payload;
            const currentQuotations = loadQuotationsFromStorage();

            // Add to beginning of array (newest first)
            newQuotations = [newQuotation, ...currentQuotations];
            saveQuotationsToStorage(newQuotations);

            console.log("CREATE_QUOTATION: Saved to storage", newQuotations);

            return {
                ...state,
                quotations: newQuotations,
                currentQuotation: newQuotation,
                loading: false
            };
        }
        case "UPDATE_QUOTATION": {
            const existingQuotations = loadQuotationsFromStorage();
            newQuotations = existingQuotations.map(q =>
                (q.POID === action.payload.id || q.id === action.payload.id)
                    ? { ...q, ...action.payload.data, updatedAt: new Date().toISOString() }
                    : q
            );
            saveQuotationsToStorage(newQuotations);
            return { ...state, quotations: newQuotations, currentQuotation: null, loading: false };
        }

        case "SEND_QUOTATION": {
            const existingQuotations = loadQuotationsFromStorage();
            newQuotations = existingQuotations.map(q =>
                (q.POID === action.payload || q.id === action.payload)
                    ? { ...q, status: QUOTATION_STATUS.SENT, sentAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
                    : q
            );
            saveQuotationsToStorage(newQuotations);
            return { ...state, quotations: newQuotations, loading: false };
        }

        case "APPROVE_QUOTATION": {
            const existingQuotations = loadQuotationsFromStorage();
            newQuotations = existingQuotations.map(q =>
                (q.POID === action.payload || q.id === action.payload)
                    ? { ...q, status: QUOTATION_STATUS.APPROVED, approvedAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
                    : q
            );
            saveQuotationsToStorage(newQuotations);
            return { ...state, quotations: newQuotations, loading: false };
        }

        case "REJECT_QUOTATION": {
            const existingQuotations = loadQuotationsFromStorage();
            newQuotations = existingQuotations.map(q =>
                (q.POID === action.payload.id || q.id === action.payload.id)
                    ? { ...q, status: QUOTATION_STATUS.REJECTED, rejectionReason: action.payload.reason, rejectedAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
                    : q
            );
            saveQuotationsToStorage(newQuotations);
            return { ...state, quotations: newQuotations, loading: false };
        }

        case "REQUEST_REVISION": {
            const existingQuotations = loadQuotationsFromStorage();
            newQuotations = existingQuotations.map(q =>
                (q.POID === action.payload.id || q.id === action.payload.id)
                    ? { ...q, status: QUOTATION_STATUS.REVISION_REQUESTED, revisionRequestReason: action.payload.reason, revisionRequestedAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
                    : q
            );
            saveQuotationsToStorage(newQuotations);
            return { ...state, quotations: newQuotations, loading: false };
        }

        case "CREATE_REVISION": {
            const existingQuotations = loadQuotationsFromStorage();
            const parentQuotation = existingQuotations.find(q => (q.POID === action.payload.parentId || q.id === action.payload.parentId));

            if (parentQuotation) {
                const revision = {
                    ...action.payload.revisionData,
                    POID: generateId(),
                    id: generateId(),
                    quotationNumber: generateQuotationNumber(),
                    parentQuotationId: parentQuotation.POID || parentQuotation.id,
                    version: parentQuotation.version + 1,
                    revisionHistory: [...(parentQuotation.revisionHistory || []), {
                        version: parentQuotation.version,
                        status: parentQuotation.status,
                        revisedAt: new Date().toISOString(),
                        reason: action.payload.revisionReason
                    }],
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    status: QUOTATION_STATUS.DRAFT
                };
                newQuotations = [revision, ...existingQuotations];
                saveQuotationsToStorage(newQuotations);
                return { ...state, quotations: newQuotations, currentQuotation: revision, loading: false };
            }
            return state;
        }

        case "CONVERT_TO_ORDER": {
            const existingQuotations = loadQuotationsFromStorage();
            newQuotations = existingQuotations.map(q =>
                (q.POID === action.payload.quotationId || q.id === action.payload.quotationId)
                    ? {
                        ...q,
                        status: QUOTATION_STATUS.CONVERTED,
                        convertedToOrderId: action.payload.orderId,
                        convertedAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString()
                    }
                    : q
            );
            saveQuotationsToStorage(newQuotations);
            return { ...state, quotations: newQuotations, loading: false };
        }

        case "DELETE_QUOTATION": {
            const existingQuotations = loadQuotationsFromStorage();
            newQuotations = existingQuotations.filter(q => (q.POID !== action.payload && q.id !== action.payload));
            saveQuotationsToStorage(newQuotations);
            return { ...state, quotations: newQuotations, loading: false };
        }

        default:
            return state;
    }
};